import { createSlice, PayloadAction } from "@reduxjs/toolkit";

const DEFAULT_OVERVIEW_CONVERSIONS_FILTERS = {
  trafficSource: null,
  country: null,
  campaign: null,
  adGroup: null,
  keyword: null,
};

interface Option {
  label: string;
  value: string;
}

const initialState = {
  isLoading: false,
  queryOutput: [] as Record<string, any>[],
  outputMetadata: {} as Record<string, any>,
  isComparisonLoading: false,
  comparisonQueryOutput: [] as Record<string, any>[],
  comparisonFetchedFor: null as string | null,
  overviewAccountSummaryData: null,
  loadingOverviewAccountSummaryData: false,
  dimensionsMobile: null,
  overviewConversionsMobileData: [],
  loadingOverviewConversionsMobileData: false,
  overviewConversionsTrafficSourceDataMobile: [],
  overviewConversionsGeoDataMobile: [],
  loadingOverviewConversionsGeoTrafficData: false,
  overviewConversionsFilterDataByIdMobileMap: {},
  overviewConversionsMobileFilters: DEFAULT_OVERVIEW_CONVERSIONS_FILTERS,
  overviewConversionsActivityNotesDataMobile: [],
  overviewConversionsNotesLabelFilter: null,
  overviewConversionsCrMobileDataRedux: [],
  overviewConversionsCrMobileLoading: false,
};

const bizDevelopmentDashboardMobileSlice = createSlice({
  name: "bizDevelopmentDashboardMobile",
  initialState,
  reducers: {
    resetMobileState: () => initialState,
    setMobileIsLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    setMobileQueryOutput: (state, action) => {
      state.queryOutput = action.payload;
    },
    setMobileOutputMetadata: (state, action) => {
      state.outputMetadata = action.payload;
    },
    setMobileIsComparisonLoading: (state, action) => {
      state.isComparisonLoading = action.payload;
    },
    setMobileComparisonQueryOutput: (state, action) => {
      state.comparisonQueryOutput = action.payload;
    },
    setMobileComparisonFetchedFor: (state, action) => {
      state.comparisonFetchedFor = action.payload;
    },
    setOverviewAccountSummaryData: (state, action) => {
      state.overviewAccountSummaryData = action.payload;
    },
    setLoadingOverviewAccountSummaryData: (state, action) => {
      state.loadingOverviewAccountSummaryData = action.payload;
    },
    setDimensionsMobile: (state, action) => {
      state.dimensionsMobile = action.payload;
    },
    setOverviewConversionsMobileData: (state, action) => {
      state.overviewConversionsMobileData = action.payload;
    },
    setLoadingOverviewConversionsMobileData: (state, action) => {
      state.loadingOverviewConversionsMobileData = action.payload;
    },
    setOverviewConversionsTrafficSourceDataMobile: (state, action) => {
      state.overviewConversionsTrafficSourceDataMobile = action.payload;
    },
    setOverviewConversionsGeoDataMobile: (state, action) => {
      state.overviewConversionsGeoDataMobile = action.payload;
    },
    setLoadingOverviewConversionsGeoTrafficData: (state, action) => {
      state.loadingOverviewConversionsGeoTrafficData = action.payload;
    },
    setOverviewConversionsFilterDataByIdMobile: (state: any, action) => {
      const { key, data } = action.payload;
      state.overviewConversionsFilterDataByIdMobileMap[key] = data;
    },
    clearOverviewConversionsFilterMobileDataMap: (state) => {
      state.overviewConversionsFilterDataByIdMobileMap = {};
    },
    setOverviewConversionsMobileFilters: (state, action) => {
      state.overviewConversionsMobileFilters = action.payload;
    },
    updateOverviewConversionsMobileFilter: (
      state: any,
      action: PayloadAction<{ key: string; value: Option | null }>,
    ) => {
      const { key, value } = action.payload;
      state.overviewConversionsMobileFilters[key] = value;
    },
    setOverviewConversionsMobileFiltersPartial: (state, action) => {
      state.overviewConversionsMobileFilters = {
        ...state.overviewConversionsMobileFilters,
        ...action.payload,
      };
    },
    resetOverviewConversionsMobileFilters: (state) => {
      state.overviewConversionsMobileFilters =
        DEFAULT_OVERVIEW_CONVERSIONS_FILTERS;
    },
    setOverviewConversionsActivityNotesDataMobile: (state, action) => {
      state.overviewConversionsActivityNotesDataMobile = action.payload;
    },
    setOverviewConversionsNotesLabelFilter: (state, action) => {
      state.overviewConversionsNotesLabelFilter = action.payload;
    },
    setOverviewConversionsCrMobileDataRedux: (state, action) => {
      state.overviewConversionsCrMobileDataRedux = action.payload;
    },
    setOverviewConversionsCrMobileLoading: (state, action) => {
      state.overviewConversionsCrMobileLoading = action.payload;
    },
  },
});

export const {
  resetMobileState,
  setMobileIsLoading,
  setMobileQueryOutput,
  setMobileOutputMetadata,
  setMobileIsComparisonLoading,
  setMobileComparisonQueryOutput,
  setMobileComparisonFetchedFor,
  setOverviewAccountSummaryData,
  setLoadingOverviewAccountSummaryData,
  setDimensionsMobile,
  setOverviewConversionsMobileData,
  setLoadingOverviewConversionsMobileData,
  setOverviewConversionsTrafficSourceDataMobile,
  setOverviewConversionsGeoDataMobile,
  setLoadingOverviewConversionsGeoTrafficData,
  setOverviewConversionsFilterDataByIdMobile,
  clearOverviewConversionsFilterMobileDataMap,
  setOverviewConversionsMobileFilters,
  updateOverviewConversionsMobileFilter,
  setOverviewConversionsMobileFiltersPartial,
  resetOverviewConversionsMobileFilters,
  setOverviewConversionsActivityNotesDataMobile,
  setOverviewConversionsNotesLabelFilter,
  setOverviewConversionsCrMobileDataRedux,
  setOverviewConversionsCrMobileLoading,
} = bizDevelopmentDashboardMobileSlice.actions;

export const selectBizMobileDashboardLoading = (state: any) =>
  state.bizDevelopmentDashboardMobile.isLoading;
export const selectBizMobileQueryOutput = (state: any) =>
  state.bizDevelopmentDashboardMobile.queryOutput;
export const selectBizMobileOutputMetadata = (state: any) =>
  state.bizDevelopmentDashboardMobile.outputMetadata;
export const selectBizMobileComparisonLoading = (state: any) =>
  state.bizDevelopmentDashboardMobile.isComparisonLoading;
export const selectBizMobileComparisonQueryOutput = (state: any) =>
  state.bizDevelopmentDashboardMobile.comparisonQueryOutput;
export const selectBizMobileComparisonFetchedFor = (state: any) =>
  state.bizDevelopmentDashboardMobile.comparisonFetchedFor;
export const selectOverviewConversionsMobileLoading = (state: any) =>
  state.bizDevelopmentDashboardMobile.loadingOverviewConversionsMobileData;
export const selectOverviewConversionsMobileData = (state: any) =>
  state.bizDevelopmentDashboardMobile.overviewConversionsMobileData;

export default bizDevelopmentDashboardMobileSlice.reducer;
